import React from "react";
import { styles } from "../../../src/styles/request_details";
import "../../styles/request_dashboard.scss";

interface Step {
    number: number;
    title: string;
    displayValue: number
}

interface RequestStepperProps {
    steps: Step[];
    currentStep: number;
    fromStatusId?: number | null;
    fromStatusCode?: string | null;
    toStatusId?: number | null;
    toStatusCode?: string | null;
}

const lastStep = { color: "#27AE60" };
const lastStepBackground = { backgroundColor: "#27AE60" };

const STATUS_TO_STEP_MAP: Record<string, number> = {
    'OPEN': 1,
    'PENDING_REVIEW': 2,
    'APPROVED': 3,
    'WORK_IN_PROGRESS': 4,
    'COMPLETED': 5,
    'CANCELLED': 5,
};

const RequestStepper: React.FC<RequestStepperProps> = ({
    steps,
    currentStep,
    fromStatusCode,
    toStatusCode,
}) => {
    const isRejected = toStatusCode?.toUpperCase() === "REJECTED";
    const isCancelled = toStatusCode?.toUpperCase() === "CANCELLED";
    const rejectedAfterStep = fromStatusCode
        ? STATUS_TO_STEP_MAP[fromStatusCode.toUpperCase()] ?? null
        : null;
    const cancelledAfterStep = fromStatusCode
        ? STATUS_TO_STEP_MAP[fromStatusCode.toUpperCase()] ?? null
        : null;
    const currentStepToDisplay = isCancelled && cancelledAfterStep ? cancelledAfterStep : currentStep;

    return (
        <>
            <div className="stepper-responsive-inner" style={styles.stepperWrapper}>
                {steps.map((step, index) => {
                    const isLastAndReached =
                        step.number === steps[steps.length - 1].number &&
                        step.number <= currentStepToDisplay;

                    const showRejectedBanner =
                        isRejected && rejectedAfterStep === step.number;

                    const showCancelledBanner =
                        isCancelled && cancelledAfterStep === step.number;

                    const isConnectorCompleted = step.number < currentStepToDisplay;

                    return (
                        <React.Fragment key={step.number}>
                            <div className="stepper-responsive-step" style={{ ...styles.stepItem, position: "relative" }}>
                                <div
                                    style={{
                                        ...styles.stepCircle,
                                        ...(step.number < currentStepToDisplay && styles.stepCircleCompleted),
                                        ...(step.number === currentStepToDisplay && !isLastAndReached && styles.stepCircleActive),
                                        ...(step.number > currentStepToDisplay && styles.stepCirclePending),
                                        ...(isLastAndReached && lastStepBackground),
                                    }}
                                >
                                    <span
                                        style={{
                                            ...styles.stepNumber,
                                            ...(step.number <= currentStepToDisplay && styles.stepNumberActive),
                                        }}
                                    >
                                        {step.displayValue}
                                    </span>
                                </div>

                                <div className="stepper-responsive-label" style={styles.stepLabel}>
                                    <div
                                        style={{
                                            ...styles.stepTitle,
                                            ...(step.number <= currentStepToDisplay && styles.stepTitleActive),
                                            ...(isLastAndReached && lastStep),
                                        }}
                                    >
                                        {step.title}
                                    </div>
                                </div>

                                {(showRejectedBanner || showCancelledBanner) && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "32px",
                                            left: "calc(100% + 8px)",
                                            transform: "translateY(-50%)",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: "4px",
                                            zIndex: 1,
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                width: "18px",
                                                height: "18px",
                                                backgroundColor: "#ef4444",
                                                borderRadius: "50%",
                                                color: "#fff",
                                                fontSize: "11px",
                                                fontWeight: 700,
                                                lineHeight: 1,
                                            }}
                                        >
                                            ✕
                                        </span>
                                        <span
                                            style={{
                                                color: "#991b1b",
                                                fontSize: "11px",
                                                fontWeight: 600,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {showCancelledBanner ? "Cancelled" : "Rejected"}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {index < steps.length - 1 && (
                                <div className="stepper-responsive-connector" style={styles.connectorWrapper}>
                                    <div style={styles.connectorLine}>
                                        {isConnectorCompleted && (
                                            <div
                                                style={{
                                                    ...styles.connectorProgress,
                                                    width: "100%",
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </>
    );
};

export default RequestStepper;